import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

// Import strings
import { NAME, PHONE_NUMBER } from '@zr/utilities/profileFields';
import {
  STRING_SCREEN_APPLY_NOW,
  STRING_SCREEN_CONTINUE,
} from '@zr/jobseeker-public/apps/jobseeker-screens/labels';
import {
  STRING_FIELD_PHONE_NUMBER_REQUIRED,
} from '@zr/components/Fields/labels';
import { FirstNameLastNameValidation } from '@jobseeker/public/apps/jobseeker-screens/validation';

// Imports form fields
import FullNameField from '@zr/components/Fields/Formik/FullNameField';
import PhoneNumberField from '@zr/components/Fields/Formik/PhoneNumberField';

// Import the JS function that updates the profile with the submitted fields
import handleUpdateProfileFormikSubmit from '@zr/jobseeker-public/apps/profile-form/utilities/handleUpdateProfileFormikSubmit';

// Import functions for telemetry
import screenTypeTelemetry from '@zr/jobseeker-public/apps/jobseeker-screens/components/Screens/ScreenTypes/screenTypeTelemetry';

// Import schema for flow parameter prop types
import { applyDataPropTypes } from '@zr/jobseeker-public/apps/jobseeker-screens/components/applyFlowPropTypes';

// Import schema for this screen type
import {
  COLLECT_NAME_AND_PHONE_NUMBER,
  collectNameAndPhoneNumberPropTypes,
} from '@zr/jobseeker-public/apps/jobseeker-screens/components/screenTypeSchema';

// The validationSchema is somewhat of a misnomer; while it does force these fields to be strings,
// validation happens on the backend when the form is submitted
const validationSchema = Yup.object({
  [NAME]: FirstNameLastNameValidation,
  [PHONE_NUMBER]: Yup.string().trim().required(STRING_FIELD_PHONE_NUMBER_REQUIRED),
});

// This is the main function for this component. It's used in the ApplyFlowApp component as
// ScreenTypeComponent
export function CollectNameAndPhoneNumber({
  onScreenTypeFinish, onError, screenTypeParameters, applyData, setIsLoading,
}) {
  // Initial values may be provided if the jobseeker already has either name or phone number
  const initialValues = {
    [NAME]: screenTypeParameters[NAME] || '',
    [PHONE_NUMBER]: screenTypeParameters[PHONE_NUMBER] || '',
  };

  // Instantiate the log methods we need from the telemetry functions
  const {
    logView,
    logFinish,
  } = screenTypeTelemetry(COLLECT_NAME_AND_PHONE_NUMBER, applyData);

  // Log the initial screen view. useEffect is a hook that is triggered by the component being
  // rendered. The logView in the array tells it to not trigger again unless logView changes
  useEffect(() => {
    logView();
  }, [logView]);

  // This adds logging to method that triggers when the form is successfully submitted.
  // The onScreenTypeFinish cuntion is provided by the caller, and in this case is almost certainly
  // the handleNextAction function in the ApplyFlowApp component.
  // The async indicates that this method is handled asyncronously, and the await means execution
  // will not continue until onScreenTypeFinish is resolved
  const handleScreenTypeFinish = async () => {
    logFinish();
    await onScreenTypeFinish();
  };


  // Defines the function that will be called when the form is submitted. This gets handed to the
  // Formik form defined in the return. The handleScreenTypeFinish function we just defined is
  // set as the onSuccess function. This completes the long chain of methods we run when the form
  // is submitted successfully:
  //
  // handleFormSubmit
  //   handleUpdateProfileFormikSubmit
  //     handleScreenTypeFinish
  //       logFinish
  //       onScreenTypeFinish
  const handleFormSubmit = handleUpdateProfileFormikSubmit({
    method: applyData.method, onError, onSuccess: handleScreenTypeFinish, setIsLoading,
  });

  // Returns the form to be rendered
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={validationSchema}
    >
      {(formikProps) => {
        const {
          handleSubmit, isSubmitting,
        } = formikProps;

        return (
          <form className="ScreenViewWrapper CollectNameAndPhoneNumber" onSubmit={handleSubmit}>
            <fieldset>
              <legend className="title">{STRING_SCREEN_APPLY_NOW}</legend>
              <FullNameField
                name={NAME}
                formikProps={formikProps}
              />
              <PhoneNumberField
                name={PHONE_NUMBER}
                formikProps={formikProps}
              />
              <div className="button_wrapper">
                <button
                  className="primary_button"
                  disabled={isSubmitting}
                  type="submit"
                >
                  {STRING_SCREEN_CONTINUE}
                </button>
              </div>
            </fieldset>
          </form>
        );
      }}
    </Formik>
  );
}

// Prop types for this component
CollectNameAndPhoneNumber.propTypes = {
  applyData: applyDataPropTypes.isRequired,
  onScreenTypeFinish: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  screenTypeParameters: collectNameAndPhoneNumberPropTypes.isRequired,
  setIsLoading: PropTypes.func,
};

CollectNameAndPhoneNumber.defaultProps = {
  setIsLoading: () => {},
};

// Export the component for use in other components
export default CollectNameAndPhoneNumber;
